import { toIsoTimestamp } from './packages-wire.js';

/**
 * Wallet top-up with Fiuu online banking (MYP-134).
 *
 * The flow, from the vendor's guide "Mypoz Fiuu 付款接口对接文档" (2026-10-26):
 *
 * 1. The app calls `molpay_pay` with the amount. The vendor records an `accdetail` as INVALID (not
 *    paid yet) and answers with `refid`, `accdetailid` and `payurl` — a link to its own gateway page.
 * 2. The browser opens `payurl`. The gateway rebuilds the amount and the customer's details from
 *    the vendor's database and forwards the customer to Fiuu's (MOLPay's) payment page.
 * 3. Fiuu tells the vendor; the vendor turns the `accdetail` into END and credits the wallet.
 * 4. The vendor sends the browser to `/wallet/top-up/result?orderid=…&accdetailid=…&status=…`.
 * 5. The result page finds the top-up in `get_trade` and shows how it really stands.
 *
 * The functions here read the vendor's answers and the return address — no React, no network — so
 * they can be tested. `code` is not read here: `legacy-api/envelope.ts` already turns every
 * refusal into a failed result.
 */

/**
 * Where a top-up stands in `get_trade`: END is `paid`, INVALID is `pending` (started, not paid).
 * Any other status was set by MyPoz staff (reconciled by hand), so it is `handled`: final, but
 * neither the app nor the customer can tell from it whether money arrived.
 */
export type FiuuTopUpStatus = 'pending' | 'paid' | 'handled';

/** The vendor's answer when a top-up is started: where to send the customer to pay. */
export interface FiuuTopUpStart {
  /** The vendor's reconciliation number, `<customer>-<accdetailid>`. */
  readonly refid: string;
  /** The top-up's own record id — what the result page looks it up by. */
  readonly accdetailid: string;
  readonly amountCents: number;
  /** The vendor's gateway page. Always passed `isSafeTopUpPayUrl` before it gets here. */
  readonly payUrl: string;
}

/** One top-up as `get_trade` reports it. */
export interface FiuuTopUpState {
  /** The record id (`id`, the `accdetailid`). */
  readonly accdetailid: string;
  /** The top-up number the customer sees (`rechargeno`, else `tradeno`), when there is one. */
  readonly reference?: string;
  readonly amountCents: number;
  readonly status: FiuuTopUpStatus;
  /** ISO 8601, set only once paid. */
  readonly paidAtIso?: string;
}

/** What the return address hints at. Only ever turns `pending` into a message; never money. */
export type TopUpReturnHint = 'cancelled' | 'failed';

/** What the result page shows. */
export type TopUpOutcome = FiuuTopUpStatus | TopUpReturnHint;

/**
 * The host the vendor's top-up guide ("Mypoz Fiuu 付款接口对接文档", 2026-10-26, section 二/三)
 * gives for both `molpay_pay` and its gateway page — the one place it is written down. The app
 * calls `molpay_pay` here (`TOP_UP_API_BASE_URL`) and only ever opens a gateway link on it
 * (`isSafeTopUpPayUrl`). It is not the main API host (`www.mypoz.com`): the vendor says the two are
 * the same server, so the customer's token works on both.
 */
export const TOP_UP_GATEWAY_HOST = 'www.mypostonline.com.my';
/** `molpay_pay`'s base, as the guide documents it: `https://www.mypostonline.com.my/OutterApi/Api/`. */
export const TOP_UP_API_BASE_URL = `https://${TOP_UP_GATEWAY_HOST}/OutterApi/Api`;
/** The gateway page's path. The gateway is the vendor's own page; Fiuu's host never appears in the link. */
export const TOP_UP_GATEWAY_PATH = '/OutterApi/Api/molpay_gateway';

type Payload = Readonly<Record<string, unknown>>;

function asPayload(value: unknown): Payload | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Payload)
    : undefined;
}

function text(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * `"100.00"`, `"100.5"` or `100` → whole cents. Undefined for anything that is not a positive
 * amount with at most two decimals: a top-up of nothing, or of a negative sum, is a broken answer,
 * not a top-up. Read from the digits, never through a float, like `toFiuuAmountParam` writes them.
 */
function positiveCents(value: unknown): number | undefined {
  const match = /^(\d{1,9})(?:\.(\d{1,2}))?$/.exec(text(value) ?? '');
  if (match === null) return undefined;
  const cents = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
  return cents > 0 ? cents : undefined;
}

/** A record id: digits only, at most 20. */
export function isAccDetailId(value: unknown): value is string {
  return typeof value === 'string' && /^\d{1,20}$/.test(value);
}

/**
 * Whether a link may be opened as the payment page: `https`, the normal port, no user or password,
 * the gateway's host and path exactly, and a `refid` and `sign` to open. Read with the `URL`
 * parser, not by matching text, because text matching is fooled by links like
 * `https://www.mypostonline.com.my@evil.example/…` (whose real host is `evil.example`).
 */
export function isSafeTopUpPayUrl(url: unknown): boolean {
  if (typeof url !== 'string') return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return (
    parsed.protocol === 'https:' &&
    parsed.username === '' &&
    parsed.password === '' &&
    parsed.port === '' && // `:443` reads as '' too; any other port is refused
    parsed.hostname === TOP_UP_GATEWAY_HOST &&
    parsed.pathname === TOP_UP_GATEWAY_PATH &&
    text(parsed.searchParams.get('refid')) !== undefined &&
    text(parsed.searchParams.get('sign')) !== undefined
  );
}

/**
 * `molpay_pay`'s answer (the whole body; its fields sit under `data`) → where to send the customer.
 * Undefined when it cannot be used: no `data`, no `accdetailid` or `refid`, no usable amount, a
 * link that is not the gateway, or an amount other than the one the customer asked for
 * (`requestedCents`) — the app must not send a customer to pay anywhere, or any sum, it cannot
 * vouch for.
 */
export function normaliseFiuuTopUpStart(
  body: unknown,
  requestedCents: number,
): FiuuTopUpStart | undefined {
  const payload = asPayload(asPayload(body)?.['data']);
  if (payload === undefined) return undefined;

  const refid = text(payload['refid']);
  const accdetailid = text(payload['accdetailid']);
  const amountCents = positiveCents(payload['amount']);
  const payUrl = text(payload['payurl']);
  if (refid === undefined || !isAccDetailId(accdetailid)) return undefined;
  if (amountCents === undefined || payUrl === undefined) return undefined;
  if (!isSafeTopUpPayUrl(payUrl)) return undefined;
  if (amountCents !== requestedCents) return undefined;

  return { refid, accdetailid, amountCents, payUrl };
}

/** `get_trade`'s `status` → ours. END is paid, INVALID (or no status) pending, anything else handled. */
export function toFiuuTopUpStatus(value: unknown): FiuuTopUpStatus {
  const code = text(value)?.toUpperCase();
  if (code === 'END') return 'paid';
  if (code === undefined || code === 'INVALID') return 'pending';
  return 'handled';
}

/**
 * Finds one top-up among `get_trade` rows (`type=CHARGE`, newest first) — by its record id when
 * there is one, and by its top-up number only when there is not. Never by either: if the address
 * carried both and they disagreed, "the first row that matches either" would depend on the order
 * of the list. Undefined when it is not among them, or has no usable amount.
 */
export function findFiuuTopUpState(
  rows: readonly unknown[],
  wanted: { readonly accdetailid?: string; readonly orderid?: string },
): FiuuTopUpState | undefined {
  for (const raw of rows) {
    const row = asPayload(raw);
    if (row === undefined) continue;

    const id = text(row['id']);
    const reference = text(row['rechargeno']) ?? text(row['tradeno']);
    const matches =
      wanted.accdetailid !== undefined
        ? id === wanted.accdetailid
        : wanted.orderid !== undefined && reference === wanted.orderid;
    if (!matches || id === undefined) continue;

    const amountCents = positiveCents(text(row['amount'])?.replace(/^[+-]/, ''));
    if (amountCents === undefined) return undefined;

    const status = toFiuuTopUpStatus(row['status']);
    const paidAt = row['handledate'] ?? row['chargedate'] ?? row['submitdate'];
    return {
      accdetailid: id,
      reference,
      amountCents,
      status,
      paidAtIso: status === 'paid' ? toIsoTimestamp(paidAt) : undefined,
    };
  }
  return undefined;
}

/**
 * The hint in the return address. The vendor sends `status=success|cancelled|failed` (and
 * `cancelled=1` on a cancel). `success` is ignored on purpose: only `get_trade` says a top-up is
 * paid. The `sign` that comes with it needs the vendor's key to check, so it is not relied on.
 */
export function toTopUpReturnHint(
  status: string | null,
  cancelled: string | null,
): TopUpReturnHint | undefined {
  if (cancelled === '1' || status === 'cancelled') return 'cancelled';
  if (status === 'failed') return 'failed';
  return undefined;
}

/**
 * What the result page shows. The hint only ever turns `pending` into `cancelled` or `failed` — a
 * message, never money: a top-up `get_trade` calls paid or handled is shown as that, whatever the
 * address says. The customer who fakes the hint misleads only themselves.
 */
export function toTopUpOutcome(
  status: FiuuTopUpStatus,
  hint: TopUpReturnHint | undefined,
): TopUpOutcome {
  return status === 'pending' && hint !== undefined ? hint : status;
}
